import { Category, TestResult } from '../types';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const updateCSVFile = async (updatedCategories: Category[], filename: string) => {
  try {
    const allScenarios = updatedCategories.flatMap(category => 
      category.scenarios.map(scenario => {
        const { Status, Notes, ...csvScenarioData } = scenario;
        return csvScenarioData;
      })
    );
    
    const csvContent = Papa.unparse(allScenarios);
    
    const API_BASE_URL = process.env.REACT_APP_API_HOST;
    const response = await fetch(`${API_BASE_URL}/scenarios/${filename}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'text/csv',
      },
      body: csvContent,
    });

    if (!response.ok) {
      throw new Error(`Błąd HTTP: ${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

export const generateNextAvailableId = (category: string, currentId: string, categories: Category[]): string => {
  const categoryScenarios = categories.find(cat => cat.name === category)?.scenarios || [];
  const idPrefix = currentId.match(/^([A-Za-z]+)/)?.[1] || '';
  
  if (!idPrefix) return currentId + "-COPY";
  
  const idsWithSamePrefix = categoryScenarios
    .map(s => s['ID testu'])
    .filter(id => id.startsWith(idPrefix));
  
  const idNumbers = idsWithSamePrefix.map(id => {
    const match = id.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  });
  
  const maxNumber = Math.max(...idNumbers, 0);
  const nextNumber = maxNumber + 1;
  
  const currentNumberFormat = currentId.match(/\d+$/)?.[0] || '';
  const paddingLength = currentNumberFormat.length || 3;
  const nextNumberFormatted = String(nextNumber).padStart(paddingLength, '0');
  
  return `${idPrefix}-${nextNumberFormatted}`;
};

export const exportFullReportCSV = (categories: Category[], filename: string, currentTest: TestResult) => {
  // Pobierz wszystkie platformy z testu lub użyj domyślnej wartości
  const platforms = currentTest.metadata?.platforms || ['Nie określono'];
  
  // Utwórz nagłówki dla podstawowych kolumn i dodatkowych kolumn dla każdej platformy
  const baseHeaders = [
    'ID testu', 
    'Kategoria', 
    'Nazwa testu', 
    'Kroki testowe', 
    'Oczekiwany rezultat', 
    'Notatki',
    'Data rozpoczęcia'
  ];
  const platformHeaders = platforms.map(platform => `Status - ${platform}`);
  const header = [...baseHeaders, ...platformHeaders, 'Status'].join(',');
  
  const allScenarios = categories.flatMap(category =>
    category.scenarios.map(scenario => ({
      ...scenario,
      categoryName: category.name
    }))
  );

  // Funkcja do konwersji statusu
  const convertStatus = (status: string | undefined): string => {
    if (status === 'Passed') return 'OK';
    return status || 'Not Tested';
  };

  // Formatuj datę
  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const rows = allScenarios.map(scenario => {
    // Podstawowe kolumny
    const baseColumns = [
      `"${scenario['ID testu']}"`,
      `"${scenario.categoryName}"`,
      `"${scenario['Nazwa testu'].replace(/"/g, '""')}"`,
      `"${scenario['Kroki testowe'].replace(/"/g, '""')}"`,
      `"${scenario['Oczekiwany rezultat'].replace(/"/g, '""')}"`,
      `"${(scenario.Notes || '').replace(/"/g, '""')}"`,
      `"${formatDate(currentTest.metadata.startDate)}"`
    ];

    // Dodaj status dla każdej platformy
    const platformColumns = platforms.map(platform => 
      `"${convertStatus(scenario.Status)}"`
    );

    // Dodaj ogólny status na końcu
    const generalStatus = `"${convertStatus(scenario.Status)}"`;

    return [...baseColumns, ...platformColumns, generalStatus].join(',');
  });
  
  const csvContent = [header, ...rows].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  const date = new Date().toISOString().slice(0, 10);
  const cleanFilename = filename.replace(/\.csv$/, '').replace(/[^\w-]/g, '_');
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${cleanFilename}_raport_${date}.csv`);
  
  document.body.appendChild(link);
  link.click();
  
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportFullReportExcel = (categories: Category[], filename: string, currentTest: TestResult) => {
  try {
    // Pobierz wszystkie platformy z testu lub użyj domyślnej wartości
    const platforms = currentTest.metadata?.platforms || ['Nie określono'];
    
    const allScenarios = categories.flatMap(category =>
      category.scenarios.map(scenario => ({
        ...scenario,
        categoryName: category.name
      }))
    );

    // Funkcja do konwersji statusu
    const convertStatus = (status: string | undefined): string => {
      if (status === 'Passed') return 'OK';
      return status || 'Not Tested';
    };

    // Formatuj datę
    const formatDate = (date: string) => {
      return new Date(date).toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    // Przygotuj dane do arkusza
    const data = allScenarios.map(scenario => {
      const row: any = {
        'ID testu': scenario['ID testu'],
        'Kategoria': scenario.categoryName,
        'Nazwa testu': scenario['Nazwa testu'],
        'Kroki testowe': scenario['Kroki testowe'],
        'Oczekiwany rezultat': scenario['Oczekiwany rezultat'],
        'Notatki': scenario.Notes || '',
        'Data rozpoczęcia': formatDate(currentTest.metadata.startDate)
      };

      // Dodaj status dla każdej platformy
      platforms.forEach(platform => {
        row[`Status - ${platform}`] = convertStatus(scenario.Status);
      });

      // Dodaj ogólny status na końcu
      row['Status'] = convertStatus(scenario.Status);

      return row;
    });

    // Utwórz nowy skoroszyt
    const wb = XLSX.utils.book_new();
    
    // Utwórz arkusz z danymi
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Ustaw szerokość kolumn
    const colWidths = [
      { wch: 15 }, // ID testu
      { wch: 20 }, // Kategoria
      { wch: 30 }, // Nazwa testu
      { wch: 50 }, // Kroki testowe
      { wch: 50 }, // Oczekiwany rezultat
      { wch: 30 }, // Notatki
      { wch: 20 }, // Data rozpoczęcia
      ...platforms.map(() => ({ wch: 15 })), // Status dla każdej platformy
      { wch: 15 }  // Status ogólny
    ];
    ws['!cols'] = colWidths;
    
    // Dodaj arkusz do skoroszytu
    XLSX.utils.book_append_sheet(wb, ws, 'Raport testów');
    
    // Zapisz plik
    const date = new Date().toISOString().slice(0, 10);
    const cleanFilename = filename.replace(/\.csv$/, '').replace(/[^\w-]/g, '_');
    const fileName = `${cleanFilename}_raport_${date}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
  } catch (error) {
    alert('Wystąpił błąd podczas eksportu do Excel.');
  }
}; 